import { PropsWithChildren, FC, useState } from 'react'
import ReleaseDFMConfirmationModal from '@Admin/AdminQuoteDetailsPage/ReleaseDFMConfirmationModal'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { imDfmFeedback } from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/__generated__/imDfmFeedback'
import imDfmFeedbackQuery from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/imDfmFeedbackQuery'
import { useDispatch } from 'react-redux'

import { actions as ImDfmActions } from '~/redux-modules/im-dfm'
import Button from '~/core/Button'
import dfmFeedbackQuery from '../Status/dfmFeedbackQuery'
import { useAllPartsReportsReleased } from '../../logic'
import { salesOrder_salesOrder_salesOrderLines_items as SalesOrderLinesItemsType } from '../Queries/__generated__/salesOrder'
import { DfmFeedback } from '~/gql/graphql'

type ParamsType = {
    orderName?: string
    id?: string
}

interface ReleaseDfmButtonPropsType {
    data: SalesOrderLinesItemsType[] // useOrderDetailData(orderName) .salesOrder
    isImOrder: boolean // will look at DFM feedback if IM
    orderId: string // passed to RleeaseDFMConfirmationModal
    quoteId: string | null // passed to RleeaseDFMConfirmationModal
    dfmFeedbacks?: DfmFeedback[]
    isOnOrderDetailsPage?: boolean
    refetchQmrQuery?: any
}

export const ReleaseDfmButton: FC<
    PropsWithChildren<ReleaseDfmButtonPropsType>
> = ({
    data: propSalesOrderData,
    isImOrder,
    orderId,
    quoteId,
    dfmFeedbacks,
    isOnOrderDetailsPage,
}) => {
    const dispatch = useDispatch()
    const [showReleaseDfmConfirmationModal, setReleaseDfmConfirmationModal] =
        useState<boolean>(false)

    const { orderName, id: qmrId } = useParams<ParamsType>()

    /**
     * Get DFM by `data` prop
     */
    const hasDfmReport = propSalesOrderData?.some(saleOrderLinesItem => {
        const reports = saleOrderLinesItem?.quoteModelRevision?.dfmReports

        return reports?.length
    })

    /**
     * Get DFM by ORDER
     */
    const { data: dfmFeedbackQueryData, refetch: refetchDfmFeedback } =
        useQuery(dfmFeedbackQuery, {
            variables: { name: orderName },
            skip: dfmFeedbacks && dfmFeedbacks?.length > 0,
        })

    const salesOrderLineItems =
        dfmFeedbackQueryData?.salesOrder?.salesOrderLines?.items

    const salesItems = hasDfmReport ? propSalesOrderData : salesOrderLineItems

    const allDfmFeedbackReleased = !useAllPartsReportsReleased(
        salesItems,
        hasDfmReport,
    )

    let isDisabled = false

    if (isOnOrderDetailsPage) {
        isDisabled = allDfmFeedbackReleased
    } else {
        isDisabled =
            dfmFeedbacks?.every(dfmFeedback => dfmFeedback?.isPublished) ||
            false
    }

    /**
     * Get DFM by QMR
     */
    const useQmrDFM = qmrId && isImOrder
    const { data: { quoteModelRevision: qmr } = {} } = useQuery<imDfmFeedback>(
        imDfmFeedbackQuery,
        {
            variables: { qmrId },
            skip: !useQmrDFM,
        },
    )

    if (useQmrDFM) {
        const hasUnpublishedImDfmFeedback = !!qmr?.dfmFeedback.some(
            qmrFeedback => qmrFeedback.isPublished === false,
        )

        isDisabled = !hasUnpublishedImDfmFeedback
    }

    function onDfmReleased() {
        if (useQmrDFM) {
            dispatch(ImDfmActions.setShouldSync(true))
        } else {
            refetchDfmFeedback()
        }
    }

    return (
        <>
            {showReleaseDfmConfirmationModal && (
                <ReleaseDFMConfirmationModal
                    onClose={() => setReleaseDfmConfirmationModal(false)}
                    isReleaseDfmOdp
                    quoteId={quoteId}
                    orderId={orderId}
                    onSubmit={onDfmReleased}
                />
            )}
            <Button
                name="release-dfm-odp"
                data-test-target="release-dfm-button-on-order-details-page"
                disabled={isDisabled}
                placement="release-dfm-odp"
                style={{ marginRight: '4px' }}
                onClick={() => setReleaseDfmConfirmationModal(true)}
            >
                Release DFM
            </Button>
        </>
    )
}

export default ReleaseDfmButton
